import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { NotificationStatusEnum, NotificationsModel } from '@models/notifications.model';
import { ResponseModel } from '@models/response.model';
import { BehaviorSubject, filter, finalize, map, merge, Observable, shareReplay, switchMap, tap, timer } from 'rxjs';
import { FCMService } from './fcm.service';
import { getCurrentContextIds } from '@shared/utils/current-context.util';

type NotificationRefreshSource = 'manual' | 'fcm' | 'poll';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private refresher$ = new BehaviorSubject(null);
  private pendingShowAllRequests = new Map<string, Observable<ResponseModel<NotificationsModel[]>>>();
  private knownNotificationIds = new Set<string>();
  private notificationsHydrated = false;
  notifications$ = merge(
    this.refresher$.pipe(map(() => 'manual' as const)),
    this.fcmService.getNotificationObservable().pipe(map(() => 'fcm' as const)),
    timer(0, 30000).pipe(
      filter(() => typeof document === 'undefined' || document.visibilityState === 'visible'),
      map(() => 'poll' as const)
    )
  ).pipe(
    switchMap((source: NotificationRefreshSource) =>
      this.show_all_notifications().pipe(
        tap(response => this.announceNewNotifications(response.data, source))
      )
    ),
    shareReplay(1)
  );

  constructor(
    private http: HttpClient,
    private route: Router,
    private fcmService: FCMService
  ) {}
  refresh() {
    this.refresher$.next(null);
  }

  getNotificationsBadgeCount(response: ResponseModel<NotificationsModel[]>): number {
    const pagination = response?.meta?.pagination;
    return pagination?.unread_count ?? pagination?.count ?? 0;
  }

  show_all_notifications(page = 1, perPage = 10): Observable<ResponseModel<NotificationsModel[]>> {
    const { restaurantId, branchId } = getCurrentContextIds();
    const requestKey = `${restaurantId}:${branchId}:${page}:${perPage}`;
    const pendingRequest = this.pendingShowAllRequests.get(requestKey);
    if (pendingRequest) {
      return pendingRequest;
    }

    const params = new HttpParams().set('page', page.toString()).set('per_page', perPage.toString());
    const request = this.http
      .get<ResponseModel<NotificationsModel[]>>(
        `restaurants/${restaurantId}/branches/${branchId}/notifications/show-all`,
        { params }
      )
      .pipe(
        finalize(() => this.pendingShowAllRequests.delete(requestKey)),
        shareReplay(1)
      );

    this.pendingShowAllRequests.set(requestKey, request);
    return request;
  }

  mark_all_as_read() {
    const { restaurantId, branchId } = getCurrentContextIds();
    return this.http.post(
      `restaurants/${restaurantId}/branches/${branchId}/notifications/mark-all-as-read?_method=PUT`,
      null
    );
  }
  notification_as_read(id: string) {
    const { restaurantId, branchId } = getCurrentContextIds();
    return this.http.post(
      `restaurants/${restaurantId}/branches/${branchId}/notifications/${id}/mark-as-read?_method=PUT`,
      null
    );
  }

  assign_to_self(notificationId: string) {
    const { restaurantId, branchId } = getCurrentContextIds();
    return this.http.post(
      `restaurants/${restaurantId}/branches/${branchId}/notifications/${notificationId}/assign-to-self`,
      {}
    );
  }

  assign_to_waiter({ notificationId, staffId }: { notificationId: string; staffId: string }) {
    const { restaurantId, branchId } = getCurrentContextIds();
    return this.http.post(
      `restaurants/${restaurantId}/branches/${branchId}/notifications/${notificationId}/assign-to-waiter`,
      {
        staff_id: staffId,
      }
    );
  }

  mark_as_done(notificationId: string) {
    const { restaurantId, branchId } = getCurrentContextIds();
    return this.http.put(
      `restaurants/${restaurantId}/branches/${branchId}/notifications/${notificationId}/mark-as-done`,
      {}
    );
  }

  private announceNewNotifications(notifications: NotificationsModel[], source: NotificationRefreshSource): void {
    const activeNotifications = notifications.filter(notification => {
      return Number(notification?.status?.key) !== NotificationStatusEnum.DONE;
    });

    if (!this.notificationsHydrated) {
      activeNotifications.forEach(notification => this.knownNotificationIds.add(notification.id));
      this.notificationsHydrated = true;
      return;
    }

    const newNotifications = activeNotifications.filter(notification => {
      return notification?.id && !this.knownNotificationIds.has(notification.id);
    });
    activeNotifications.forEach(notification => this.knownNotificationIds.add(notification.id));

    if (source !== 'poll' || newNotifications.length < 1) {
      return;
    }

    const notification = newNotifications[0];
    this.fcmService.showNotification(
      notification.data?.title || 'New notification',
      notification.data?.body || '',
      'info'
    );
  }
}
